import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { useState, useEffect } from 'react';
import { FREE_MAIL_CONTACT_LIMIT } from '../types/mailCampaign';

interface PlanFeatures {
  max_accounts: number;
  max_transactions?: number;
  max_transactions_per_month?: number;
  max_currencies: number;
  max_purchases: number;
  max_clients: number;
  max_mail_contacts: number;
  analytics: boolean;
  priority_support: boolean;
  export_data: boolean;
  custom_categories: boolean;
  lend_borrow: boolean;
  last_wish: boolean;
  advanced_charts: boolean;
  advanced_reporting: boolean;
}

interface UsageStats {
  accounts: {
    current: number;
    limit: number;
    percentage: number;
  };
  currencies: {
    current: number;
    limit: number;
    percentage: number;
  };
  transactions: {
    current: number;
    limit: number;
    percentage: number;
  };
  purchases: {
    current: number;
    limit: number;
    percentage: number;
  };
  clients: {
    current: number;
    limit: number;
    percentage: number;
  };
  current_month_transactions?: number;
  max_transactions_per_month?: number;
  percentage_used?: number;
  transactions_remaining?: number;
  days_remaining_in_month?: number;
  reset_date?: string;
}

export const usePlanFeatures = () => {
  const { user, profile } = useAuthStore();
  const [features, setFeatures] = useState<PlanFeatures | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  const currentPlan = profile?.subscription?.plan || 'free';
  const isFreePlan = currentPlan === 'free';
  const isPremiumPlan = currentPlan === 'premium';

  useEffect(() => {
    if (user) {
      loadPlanFeatures();
      loadUsageStats();
    }
  }, [user, profile]);

  const loadPlanFeatures = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_plan_features', { user_uuid: user?.id });

      if (error) throw error;
      setFeatures({
        ...data,
        max_mail_contacts:
          data?.max_mail_contacts ??
          (profile?.subscription?.plan === 'premium' ? -1 : FREE_MAIL_CONTACT_LIMIT),
      });
    } catch (error) {

      // Set default free features
      setFeatures({
        max_accounts: 3,
        max_transactions_per_month: 25,
        max_currencies: 1,
        max_purchases: 50,
        max_clients: 5,
        max_mail_contacts: FREE_MAIL_CONTACT_LIMIT,
        analytics: false,
        priority_support: false,
        export_data: false,
        custom_categories: false,
        lend_borrow: false,
        last_wish: false,
        advanced_charts: false,
        advanced_reporting: false,
      });
    }
  };

  const loadUsageStats = async () => {
    try {
      // Get monthly transaction stats
      const { data: monthlyStats, error: monthlyError } = await supabase
        .rpc('get_monthly_usage_stats', { user_uuid: user?.id });

      if (monthlyError) throw monthlyError;

      // Get other usage stats (accounts, currencies, purchases)
      const { data: otherStats, error: otherError } = await supabase
        .rpc('get_user_usage_stats', { user_uuid: user?.id });

      if (otherError) throw otherError;

      // Combine the stats
      const combinedStats = {
        ...otherStats,
        // Override transactions with monthly data
        current_month_transactions: monthlyStats?.current_month_transactions || 0,
        max_transactions_per_month: monthlyStats?.max_transactions_per_month || 25,
        percentage_used: monthlyStats?.percentage_used || 0,
        transactions_remaining: monthlyStats?.transactions_remaining || 25,
        days_remaining_in_month: monthlyStats?.days_remaining_in_month || 30,
        reset_date: monthlyStats?.reset_date
      };

      setUsageStats(combinedStats);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (featureName: keyof PlanFeatures): boolean => {
    if (!features) return false;
    return features[featureName] === true;
  };

  const canCreateAccount = (): boolean => {
    if (!usageStats) return true;
    return usageStats.accounts.limit === -1 || usageStats.accounts.current < usageStats.accounts.limit;
  };

  const canAddCurrency = (currency: string): boolean => {
    if (!usageStats) return true;
    if (usageStats.currencies.limit === -1) return true;
    
    // Check if currency already exists
    // This would need to be implemented with actual currency data
    return usageStats.currencies.current < usageStats.currencies.limit;
  };

  const canCreateTransaction = (): boolean => {
    if (!usageStats) return true;
    return usageStats.transactions.limit === -1 || usageStats.transactions.current < usageStats.transactions.limit;
  };

  const canCreatePurchase = (): boolean => {
    if (!usageStats) return true;
    return usageStats.purchases.limit === -1 || usageStats.purchases.current < usageStats.purchases.limit;
  };

  const canCreateClient = (): boolean => {
    if (!usageStats || !usageStats.clients) return true;
    return usageStats.clients.limit === -1 || usageStats.clients.current < usageStats.clients.limit;
  };

  const isNearLimit = (type: 'accounts' | 'currencies' | 'transactions' | 'purchases' | 'clients'): boolean => {
    if (!usageStats) return false;
    const stats = usageStats[type];
    return stats.percentage >= 80 && stats.percentage < 100;
  };

  const isAtLimit = (type: 'accounts' | 'currencies' | 'transactions' | 'purchases' | 'clients'): boolean => {
    if (!usageStats) return false;
    const stats = usageStats[type];
    return stats.percentage >= 100;
  };

  const getUpgradeMessage = (feature: string): string => {
    const messages: Record<string, string> = {
      custom_categories: 'Custom categories are a Premium feature. Upgrade to create unlimited custom categories.',
      lend_borrow: 'Lend & Borrow tracking is a Premium feature. Upgrade to track your loans and borrowings.',
      last_wish: 'Last Wish - Digital Time Capsule is a Premium feature. Upgrade to secure your digital legacy.',
      export_data: 'Data export is a Premium feature. Upgrade to export your data in CSV, Excel, or PDF formats.',
      advanced_analytics: 'Advanced analytics are a Premium feature. Upgrade for detailed insights and forecasting.',
      unlimited_accounts: 'You\'ve reached your account limit. Upgrade to Premium for unlimited accounts.',
      unlimited_currencies: 'You\'ve reached your currency limit. Upgrade to Premium for unlimited currencies.',
      unlimited_transactions: 'You\'ve reached your monthly transaction limit. Upgrade to Premium for unlimited transactions.',
      unlimited_purchases: 'You\'ve reached your purchase limit. Upgrade to Premium for unlimited purchases.',
      unlimited_clients: 'You\'ve reached your client limit. Upgrade to Premium for unlimited clients.',
      unlimited_mail_contacts: `You've reached your email list limit (${FREE_MAIL_CONTACT_LIMIT}). Upgrade to Premium for unlimited contacts.`,
    };
    return messages[feature] || 'This feature requires a Premium plan.';
  };

  return {
    // Plan info
    currentPlan,
    isFreePlan,
    isPremiumPlan,
    
    // Features
    features,
    hasFeature,
    
    // Usage
    usageStats,
    loading,
    canCreateAccount,
    canAddCurrency,
    canCreateTransaction,
    canCreatePurchase,
    canCreateClient,
    isNearLimit,
    isAtLimit,
    
    // Utilities
    getUpgradeMessage,
    loadUsageStats, // Allow manual refresh
  };
}; 

